import {
  ISKill,
  useCheckChannelMutation,
  useCheckOperatorMutation,
  useStoreCmbMutation,
  AppMobile,
} from '@vfit/shared/data-access';
import { ERROR_CODE_DEFAULT, IClickToCallMeData, IErrorItem } from '@vfit/shared/models';
import { useEffect, useState } from 'react';
import { erroreMessages, ICodeResponse, IVolaService } from './useVola.models';

export const useVolaService = (
  skill: ISKill,
  desc: string,
  telephoneNumber: string,
  timeSlot: string,
  cmsErrors?: IClickToCallMeData['clicktocall']['errorSubmission']
) => {
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [codeResponse, setCodeResponse] = useState<ICodeResponse | null>(null);
  const [responseTitle, setResponseTitle] = useState<string>('');
  const [responseMessage, setResponseMessage] = useState<string>('');
  const [responseTransaction, setResponseTransaction] = useState<string>('');
  const [volaService, setVolaService] = useState<IVolaService | ''>('cmn');

  const getErrorCmsVolaByCode = (
    cmsErrors: IClickToCallMeData['clicktocall']['errorSubmission'],
    errorCode: string
  ): IErrorItem | undefined => {
    const itemToReturn = cmsErrors?.find?.(
      (cmsError) => cmsError?.code?.toLowerCase() === String(errorCode)?.toLowerCase()
    );
    if (!itemToReturn)
      return cmsErrors?.find?.((cmsError) => cmsError?.code === ERROR_CODE_DEFAULT);
    return itemToReturn;
  };

  const {
    mutate: checkChannelMutation,
    isLoading: checkChannelIsLoading,
    isError: checkChannelIsError,
    error: checkChannelError,
    isSuccess: checkChannelIsSuccess,
    data: checkChannelData,
  } = useCheckChannelMutation();

  const {
    mutate: checkOperatorMutation,
    isError: checkOperatorIsError,
    error: checkOperatorError,
    isSuccess: checkOperatorIsSuccess,
    data: checkOperatorData,
  } = useCheckOperatorMutation();

  const {
    mutate: storeCmbMutation,
    isError: storeCmbIsError,
    isSuccess: storeCmbIsSuccess,
    isLoading: storeCmbIsLoading,
    error: storeCmbError,
    data: storeCmbData,
  } = useStoreCmbMutation();

  useEffect(() => {
    if (checkChannelIsSuccess) {
      setIsLoading(false);
    }
  }, [checkChannelIsSuccess]);

  useEffect(() => {
    if (
      volaService === 'cmn' &&
      telephoneNumber &&
      !AppMobile.checkIsApp() &&
      !checkOperatorIsSuccess &&
      !checkChannelIsError
    ) {
      checkOperatorMutation({ id_channel: skill, telephone_number: telephoneNumber, desc });
      setIsLoading(true);
    }
  }, [volaService, telephoneNumber]);

  useEffect(() => {
    if (checkOperatorIsSuccess && checkOperatorData?.code_response === '000') {
      setVolaService('cmn');
      setIsSuccess(true);
      setIsLoading(false);
      setCodeResponse(checkOperatorData?.code_response as unknown as ICodeResponse);
      setResponseMessage(checkOperatorData?.description);
      setResponseTransaction(checkOperatorData?.id_request);
    }
    if (
      (checkOperatorIsSuccess && checkOperatorData?.code_response === '001') ||
      checkOperatorData?.code_response === '011'
    ) {
      setVolaService('cmb');
      setIsLoading(false);
      setCodeResponse(checkOperatorData?.code_response as unknown as ICodeResponse);
      setResponseMessage(checkOperatorData?.description);
      setResponseTransaction(checkOperatorData?.id_request);
    }
  }, [checkOperatorIsSuccess, checkOperatorData]);

  useEffect(() => {
    if (
      telephoneNumber &&
      timeSlot &&
      volaService === 'cmb' &&
      ((checkOperatorIsSuccess && checkOperatorData?.code_response === '001') ||
        (checkChannelIsSuccess && checkChannelData?.code_response === '011') ||
        (checkChannelIsSuccess && checkChannelData?.code_response === '000'))
    ) {
      storeCmbMutation({
        id_channel: skill,
        desc,
        telephone_number: telephoneNumber,
        contact_time: timeSlot,
      });
      setIsLoading(true);
    }
  }, [volaService, timeSlot]);

  useEffect(() => {
    if (storeCmbIsSuccess && storeCmbData?.code_response === '000') {
      setCodeResponse(storeCmbData?.code_response as ICodeResponse);
      setResponseMessage(storeCmbData?.description);
      setResponseTransaction(storeCmbData?.id_request);
      setIsSuccess(true);
      setIsLoading(false);
    }
    if (storeCmbIsSuccess && storeCmbData?.code_response !== '000') {
      setCodeResponse(storeCmbData?.code_response as ICodeResponse);
      setResponseMessage(storeCmbData?.description);
      setResponseTransaction(storeCmbData?.id_request);
      setIsSuccess(false);
      setIsError(true);
      setIsLoading(false);
    }
  }, [checkChannelIsSuccess, storeCmbIsSuccess, storeCmbData]);

  // Error Handling
  useEffect(() => {
    if (
      (checkChannelIsError || checkOperatorIsError) &&
      (checkChannelError || checkOperatorError)
    ) {
      setIsSuccess(false);
      setIsLoading(false);
      setIsError(true);
      const codeRes = (checkChannelError || checkOperatorError)?.body?.code_response;
      const errorMessToDisplay = erroreMessages[codeRes?.toString() || ''];
      setCodeResponse(codeRes);
      setResponseTitle(errorMessToDisplay?.title);
      setResponseMessage(errorMessToDisplay?.description);
    }
  }, [
    checkChannelIsError,
    checkOperatorIsError,
    storeCmbIsError,
    checkChannelError,
    checkOperatorError,
  ]);

  useEffect(() => {
    if (storeCmbIsError || checkOperatorIsError || checkChannelIsError) {
      const codeResponse =
        (checkChannelError || checkOperatorError || storeCmbError)?.body?.code_response ||
        storeCmbError?.status ||
        checkOperatorError?.status ||
        checkChannelError?.status;
      setCodeResponse(codeResponse as unknown as ICodeResponse);
      setIsError(true);
      const errorForCode = cmsErrors ? getErrorCmsVolaByCode(cmsErrors, codeResponse) : undefined;
      setResponseTitle(errorForCode?.headline || 'Ops!');
      setResponseMessage(
        errorForCode?.errorMessage ||
          'Stiamo riscontrando problemi tecnici. Ti invitiamo a riprovare più tardi.'
      );
    }
  }, [storeCmbIsError, checkOperatorIsError, checkChannelIsError]);

  useEffect(() => {
    if (storeCmbIsLoading) {
      setIsLoading(true);
    }
  }, [storeCmbIsLoading]);

  useEffect(() => {
    if (skill && !checkChannelIsLoading) {
      setIsLoading(true);
    }
  }, []);

  return {
    codeResponse,
    responseTitle,
    responseMessage,
    responseTransaction,
    checkChannelMutation,
    checkChannelIsSuccess,
    checkChannelData,
    checkOperatorIsSuccess,
    storeCmbMutation,
    isLoading,
    isError,
    isSuccess,
    volaService,
  };
};
